import { useState, useEffect, useCallback } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, ApiError, errorMessage } from "./api";
import "./StatsDashboard.css";

// Couleurs de données validées (contraste + CVD) sur le fond crème — plus
// claires que les couleurs de marque, réservées aux marques graphiques.
const DATA_GREEN = "#379469";
const DATA_TERRACOTTA = "#c05f2e";
const DATA_GRAY = "#8a8f83";

const PLATFORM_COLORS = { Android: DATA_GREEN, Apple: DATA_TERRACOTTA, Autre: DATA_GRAY };

function niceEvenMax(m) {
  let scale = 1;
  while (m > 10 * scale) scale *= 10;
  for (const c of [2, 4, 6, 8, 10]) {
    if (c * scale >= m) return c * scale;
  }
  return 10 * scale;
}

function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "short" });
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// Colonnes : ≤24px de large, sommet arrondi 4px, base carrée, grille hairline.
// Générique : `data` = [{ label, count }], `unit` = mot du tooltip (accordé).
function BarChart({ data, ariaLabel, unit = "carte" }) {
  const [hover, setHover] = useState(null);

  const W = 560, H = 220, padL = 36, padR = 8, padT = 14, padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxCount = Math.max(1, ...data.map((d) => d.count));
  const yMax = niceEvenMax(maxCount);
  const ticks = [0, yMax / 2, yMax];
  const band = innerW / data.length;
  const barW = Math.min(24, band * 0.6);
  const baseY = padT + innerH;
  const yFor = (v) => padT + innerH * (1 - v / yMax);
  const maxIdx = data.reduce((best, d, i) => (d.count > data[best].count ? i : best), 0);

  const barPath = (x, y) => {
    const r = Math.min(4, baseY - y);
    return `M ${x} ${baseY} L ${x} ${y + r} Q ${x} ${y} ${x + r} ${y}
            L ${x + barW - r} ${y} Q ${x + barW} ${y} ${x + barW} ${y + r}
            L ${x + barW} ${baseY} Z`;
  };

  return (
    <div className="sd-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="sd-chart" role="img" aria-label={ariaLabel}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={yFor(t)} y2={yFor(t)} className="sd-gridline" />
            <text x={padL - 6} y={yFor(t) + 3.5} className="sd-tick" textAnchor="end">{t}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const x = padL + band * i + (band - barW) / 2;
          const y = yFor(d.count);
          return (
            <g key={d.label}>
              {d.count > 0 && <path d={barPath(x, y)} fill={DATA_GREEN} />}
              {/* Libellé direct sur le maximum uniquement ; le tooltip porte le reste */}
              {i === maxIdx && d.count > 0 && (
                <text x={x + barW / 2} y={y - 5} className="sd-bar-label" textAnchor="middle">{d.count}</text>
              )}
              <text x={padL + band * i + band / 2} y={H - 8} className="sd-tick" textAnchor="middle">
                {d.label}
              </text>
              {/* Cible de survol plus large que la barre */}
              <rect
                x={padL + band * i} y={padT} width={band} height={innerH + padB}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        // Position calculée dans le repère du SVG (padding compris), sinon le
        // tooltip est décalé par rapport à la barre survolée.
        <div className="sd-tooltip" style={{ left: `${((padL + band * (hover + 0.5)) / W) * 100}%` }}>
          <span className="sd-tooltip-label">{data[hover].tip || data[hover].label}</span>
          <span className="sd-tooltip-value">
            {data[hover].count} {unit}{data[hover].count > 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

// Barre empilée horizontale : segments séparés par un liseré 2px couleur du
// fond ; l'identité passe par la légende, jamais par la couleur seule.
function PlatformSplit({ platforms, total }) {
  if (!total) return null;
  return (
    <div>
      <div className="sd-split">
        {platforms.map((p) => (
          <div
            key={p.platform}
            className="sd-split-seg"
            style={{ width: `${(p.count / total) * 100}%`, background: PLATFORM_COLORS[p.platform] }}
            title={`${p.platform} : ${p.count}`}
          />
        ))}
      </div>
      <div className="sd-legend">
        {platforms.map((p) => (
          <span key={p.platform} className="sd-legend-item">
            <span className="sd-swatch" style={{ background: PLATFORM_COLORS[p.platform] }} />
            {p.platform} · {p.count}
          </span>
        ))}
      </div>
    </div>
  );
}

function StatsDashboard({ user, onNavigate, onLogout }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [cardsTotal, setCardsTotal] = useState(0);
  const [cardsPage, setCardsPage] = useState(1);
  const [cardsPages, setCardsPages] = useState(1);
  const [cardSearch, setCardSearch] = useState("");

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError("");
    setForbidden(false);
    try {
      const data = await apiGet(`/api/organizations/${user.organization_id}/stats`,
        "Impossible de charger les statistiques");
      setStats(data);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
      } else {
        setError(errorMessage(e, "Impossible de charger les statistiques"));
      }
    }
    setLoading(false);
  }, [user.organization_id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Liste complète des cartes du commerce, paginée et filtrable — distincte
  // du top 5 renvoyé avec les statistiques.
  const fetchCards = useCallback(async () => {
    const params = new URLSearchParams({ page: cardsPage, limit: 20 });
    if (cardSearch.trim()) params.append("search", cardSearch.trim());
    try {
      const data = await apiGet(
        `/api/organizations/${user.organization_id}/cards?${params}`,
        "Impossible de charger les cartes");
      setCards(data.cards);
      setCardsTotal(data.total);
      setCardsPages(data.pages || 1);
    } catch {
      // Le blocage éventuel (plan, organisation) est déjà signalé par l'appel
      // aux statistiques : inutile d'afficher deux fois la même erreur.
      setCards([]);
      setCardsTotal(0);
    }
  }, [user.organization_id, cardsPage, cardSearch]);

  // Debounce : sans lui, une requête partirait à chaque frappe
  useEffect(() => {
    const timer = setTimeout(fetchCards, 300);
    return () => clearTimeout(timer);
  }, [fetchCards]);

  const isStamps = stats?.card_mode !== "points";

  return (
    <div className="sd-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Statistiques"
        onBack={() => onNavigate("home")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />

      <main className="sd-main">
        {loading ? (
          <p className="sd-loading">Chargement...</p>
        ) : forbidden ? (
          <div className="sd-panel sd-locked">
            <h2>Fonctionnalité Premium</h2>
            <p>Le tableau de bord statistiques est réservé aux plans Premium et Extra.</p>
          </div>
        ) : error ? (
          <div className="sd-panel sd-error">{error}</div>
        ) : (
          <>
            <div className="sd-tiles">
              <div className="sd-tile">
                <span className="sd-tile-label">Clients</span>
                <span className="sd-tile-value">{stats.total_cards}</span>
                <span className="sd-tile-hint">{stats.active_cards} carte{stats.active_cards > 1 ? "s" : ""} active{stats.active_cards > 1 ? "s" : ""}</span>
              </div>
              <div className="sd-tile">
                <span className="sd-tile-label">Nouveaux clients</span>
                <span className="sd-tile-value">{stats.new_30d}</span>
                <span className="sd-tile-hint">30 derniers jours</span>
              </div>
              <div className="sd-tile">
                <span className="sd-tile-label">Clients actifs</span>
                <span className="sd-tile-value">{stats.visited_30d}</span>
                <span className="sd-tile-hint">passés en 30 jours</span>
              </div>
              <div className="sd-tile">
                <span className="sd-tile-label">{isStamps ? "Récompenses complétées" : "Points distribués"}</span>
                <span className="sd-tile-value">{isStamps ? stats.rewards : stats.total_points}</span>
                <span className="sd-tile-hint">{isStamps ? "cartes de 10 tampons" : "cumul de tous les clients"}</span>
              </div>
              <div className="sd-tile">
                <span className="sd-tile-label">Cette semaine</span>
                <span className="sd-tile-value">{stats.new_7d}</span>
                <span className="sd-tile-hint">nouvelles cartes en 7 jours</span>
              </div>
              <div className="sd-tile">
                <span className="sd-tile-label">Moyenne par client</span>
                <span className="sd-tile-value">
                  {stats.avg_points.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}
                </span>
                <span className="sd-tile-hint">{isStamps ? "tampons cumulés" : "points en cagnotte"}</span>
              </div>
              {!isStamps && (
                <div className="sd-tile">
                  <span className="sd-tile-label">Points par scan</span>
                  <span className="sd-tile-value">
                    {stats.avg_points_per_scan == null
                      ? "—"
                      : stats.avg_points_per_scan.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}
                  </span>
                  <span className="sd-tile-hint">moyenne des points attribués</span>
                </div>
              )}
              {isStamps ? (
                <div className="sd-tile">
                  <span className="sd-tile-label">Proches de la récompense</span>
                  <span className="sd-tile-value">{stats.near_reward}</span>
                  <span className="sd-tile-hint">8 tampons ou plus sur la carte</span>
                </div>
              ) : (
                <div className="sd-tile">
                  <span className="sd-tile-label">Meilleure cagnotte</span>
                  <span className="sd-tile-value">{stats.max_points}</span>
                  <span className="sd-tile-hint">points du meilleur client</span>
                </div>
              )}
              <div className="sd-tile">
                <span className="sd-tile-label">Jamais revenus</span>
                <span className="sd-tile-value">{stats.never_visited}</span>
                <span className="sd-tile-hint">aucun passage depuis l'inscription</span>
              </div>
            </div>

            <div className="sd-panel">
              <h2>Nouvelles cartes par mois</h2>
              <BarChart
                data={stats.monthly.map((m) => ({ label: monthLabel(m.month), count: m.count }))}
                ariaLabel="Nouvelles cartes créées par mois sur les six derniers mois"
                unit="carte"
              />
            </div>

            <div className="sd-panel">
              <h2>{isStamps ? "Progression sur la carte en cours" : "Répartition des cagnottes"}</h2>
              {stats.total_cards === 0 ? (
                <p className="sd-empty">Aucune carte pour le moment</p>
              ) : (
                <BarChart
                  data={stats.distribution.map((d) => ({
                    ...d,
                    tip: isStamps
                      ? `${d.label} tampon${d.label === "1" ? "" : "s"}`
                      : ({ "0": "0 point", "1-9": "1 à 9 points", "100+": "100 points et plus" }[d.label]
                        || `${d.label} points`),
                  }))}
                  ariaLabel={isStamps
                    ? "Nombre de clients par nombre de tampons sur la carte en cours"
                    : "Nombre de clients par tranche de points"}
                  unit="client"
                />
              )}
            </div>

            <div className="sd-row">
              <div className="sd-panel">
                <h2>Répartition par plateforme</h2>
                {stats.platforms.length === 0 ? (
                  <p className="sd-empty">Aucune carte pour le moment</p>
                ) : (
                  <PlatformSplit platforms={stats.platforms} total={stats.total_cards} />
                )}
              </div>

              <div className="sd-panel">
                <h2>Meilleurs clients</h2>
                {stats.top_clients.length === 0 ? (
                  <p className="sd-empty">Aucune carte pour le moment</p>
                ) : (
                  <table className="sd-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th className="sd-num">{isStamps ? "Tampons" : "Points"}</th>
                        <th>Dernier passage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_clients.map((c, i) => (
                        <tr key={i}>
                          <td>{c.surname} {c.name}</td>
                          <td className="sd-num">{c.points}</td>
                          <td>{formatDate(c.last_visit_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="sd-panel sd-cards-panel">
              <div className="sd-cards-head">
                <h2>
                  Cartes de fidélité
                  {cardsTotal > 0 && (
                    <span className="sd-cards-count">{cardsTotal}</span>
                  )}
                </h2>
                <input
                  type="search"
                  className="sd-cards-search"
                  placeholder="Rechercher un client (nom, e-mail, n° de carte)..."
                  value={cardSearch}
                  onChange={(e) => { setCardSearch(e.target.value); setCardsPage(1); }}
                  aria-label="Rechercher une carte"
                />
              </div>

              {cards.length === 0 ? (
                <p className="sd-empty">
                  {cardSearch.trim()
                    ? "Aucune carte ne correspond à cette recherche."
                    : "Aucune carte pour le moment"}
                </p>
              ) : (
                <>
                  <table className="sd-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>N° de carte</th>
                        <th className="sd-num">{isStamps ? "Tampons" : "Points"}</th>
                        <th>Dernier passage</th>
                        <th>Inscription</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <span className="sd-card-client">{c.surname} {c.name}</span>
                            <span className="sd-card-email">{c.email}</span>
                          </td>
                          <td className="sd-card-id">{c.id}</td>
                          <td className="sd-num">{c.points}</td>
                          <td>{formatDate(c.last_visit_at)}</td>
                          <td>{formatDate(c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {cardsPages > 1 && (
                    <div className="sd-pagination">
                      <button
                        disabled={cardsPage <= 1}
                        onClick={() => setCardsPage(cardsPage - 1)}
                      >
                        Précédent
                      </button>
                      <span>{cardsPage} / {cardsPages}</span>
                      <button
                        disabled={cardsPage >= cardsPages}
                        onClick={() => setCardsPage(cardsPage + 1)}
                      >
                        Suivant
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default StatsDashboard;
